import { ActionButton, Stack } from '@fluentui/react';
import { Css } from 'nano-css/addon/vcssom/cssToTree';
import React from 'react';
import { hasLocalOverrides } from '../stylesConfig';

const CSS_LOCAL_STORAGE_KEY = 'storefront-editor-css';
const CSS_PASTE_MAX_AGE_MS = 60 * 60 * 1000;

interface IStoredCssPayload {
    css: Css;
    copiedAt: number;
}

const getStoredCssPayload = (): IStoredCssPayload | null => {
    const text = localStorage.getItem(CSS_LOCAL_STORAGE_KEY);
    if (!text) return null;

    try {
        const parsed = JSON.parse(text) as Partial<IStoredCssPayload>;
        if (!parsed || !parsed.css || typeof parsed.copiedAt !== 'number') {
            return null;
        }

        return { css: parsed.css, copiedAt: parsed.copiedAt };
    } catch {
        return null;
    }
};

const isCssPayloadPastable = (payload: IStoredCssPayload | null): boolean => {
    if (!payload) return false;
    return Date.now() - payload.copiedAt < CSS_PASTE_MAX_AGE_MS;
};

interface ICssCopyPasteProps {
    css: Css | undefined;
    onPaste: (newCss: Css) => void;
    // Optional "Clear style": wipes the local overrides (a $styleRef on the dict is kept — the
    // element falls back to the named style's values). Button renders only when provided, and is
    // disabled when there is nothing beyond the ref to clear.
    onClear?: () => void;
}

const CssCopyPasteButtons: React.FC<ICssCopyPasteProps> = (props) => {
    const [hasPastableStoredCss, setHasPastableStoredCss] = React.useState<boolean>(() => {
        return isCssPayloadPastable(getStoredCssPayload());
    });

    React.useEffect(() => {
        const refreshPastableState = () => {
            setHasPastableStoredCss(isCssPayloadPastable(getStoredCssPayload()));
        };

        const handleStorage = (event: StorageEvent) => {
            if (event.key !== CSS_LOCAL_STORAGE_KEY) return;
            refreshPastableState();
        };

        const intervalId = window.setInterval(refreshPastableState, 60 * 1000);
        window.addEventListener('storage', handleStorage);
        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('storage', handleStorage);
        };
    }, []);

    const handleCopy = () => {
        if (!props.css) return;
        const payload: IStoredCssPayload = {
            css: props.css,
            copiedAt: Date.now(),
        };
        const cssString = JSON.stringify(payload);
        localStorage.setItem(CSS_LOCAL_STORAGE_KEY, cssString);
        setHasPastableStoredCss(true);
    };

    const handlePaste = () => {
        const payload = getStoredCssPayload();
        if (!payload) return;
        if (!isCssPayloadPastable(payload)) {
            setHasPastableStoredCss(false);
            return;
        }

        try {
            props.onPaste(payload.css);
        } catch (e) {
            alert('Invalid CSS data in local storage');
        }
    }

    return <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: '0.5em' }}>
        <ActionButton
            iconProps={{ iconName: 'Copy' }}
            onClick={handleCopy}
            disabled={!props.css}
            text='Copy style'
        />
        <ActionButton
            iconProps={{ iconName: 'Paste' }}
            onClick={handlePaste}
            disabled={!hasPastableStoredCss}
            text='Paste style'
        />
        {props.onClear && <ActionButton
            iconProps={{ iconName: 'ClearFormatting' }}
            onClick={props.onClear}
            disabled={!hasLocalOverrides(props.css)}
            text='Clear style'
        />}
    </Stack>
}

export default CssCopyPasteButtons;
